/** The lifecycle phase of a tool call, mapped from the AI SDK tool-part `state`. */
export type ToolCallPhase = 'running' | 'done' | 'failed';

interface VerbForms {
  /** Completed, past tense — "Checked documents". */
  done: string;
  /** In-flight, present continuous — "Checking documents". */
  running: string;
  /** Bare infinitive for the failed line — "Failed to check documents". */
  infinitive: string;
}

/** Turns MCP tool names (`verb_noun`, e.g. `list_documents`) into human-readable activity labels for
 * the chat tool-call rows. Verb prefix → tense forms; the remaining segments become the noun. Falls
 * back to a generic "Ran …" phrasing for names that don't follow the convention. */
export class ToolCallHelper {
  private static readonly VERBS: Record<string, VerbForms> = {
    list: { done: 'Checked', running: 'Checking', infinitive: 'check' },
    get: { done: 'Looked up', running: 'Looking up', infinitive: 'look up' },
    search: { done: 'Searched', running: 'Searching', infinitive: 'search' },
    create: { done: 'Created', running: 'Creating', infinitive: 'create' },
    update: { done: 'Updated', running: 'Updating', infinitive: 'update' },
    add: { done: 'Added', running: 'Adding', infinitive: 'add' },
    set: { done: 'Set', running: 'Setting', infinitive: 'set' },
    remove: { done: 'Removed', running: 'Removing', infinitive: 'remove' },
    post: { done: 'Posted', running: 'Posting', infinitive: 'post' },
    assign: { done: 'Assigned', running: 'Assigning', infinitive: 'assign' },
  };

  private static readonly FALLBACK: VerbForms = {
    done: 'Ran',
    running: 'Running',
    infinitive: 'run',
  };

  private static readonly CONNECTOR_TOOL_PREFIX = 'COMPOSIO_';

  /** Exact tool names whose labels can't be derived from the name — connector tools and tools whose
   * name carries an internal vendor the user never sees. */
  private static readonly TOOL_LABEL_OVERRIDES: Record<string, VerbForms> = {
    COMPOSIO_SEARCH_TOOLS: {
      done: 'Checked your connected apps',
      running: 'Checking your connected apps',
      infinitive: 'check your connected apps',
    },
    COMPOSIO_GET_TOOL_SCHEMAS: {
      done: 'Prepared a connected app action',
      running: 'Preparing a connected app action',
      infinitive: 'prepare a connected app action',
    },
    COMPOSIO_MULTI_EXECUTE_TOOL: {
      done: 'Ran a connected app action',
      running: 'Running a connected app action',
      infinitive: 'run a connected app action',
    },
    list_rootfi_connections: {
      done: 'Checked connected integrations',
      running: 'Checking connected integrations',
      infinitive: 'check connected integrations',
    },
    query_rootfi_data: {
      done: 'Read integration data',
      running: 'Reading integration data',
      infinitive: 'read integration data',
    },
    get_rootfi_financial_statement: {
      done: 'Looked up financial statement',
      running: 'Looking up financial statement',
      infinitive: 'look up financial statement',
    },
  };

  private static readonly CONNECTOR_FALLBACK: VerbForms = {
    done: 'Used a connected app',
    running: 'Using a connected app',
    infinitive: 'use a connected app',
  };

  private static overrideVerb(toolName: string): VerbForms | undefined {
    const override = ToolCallHelper.TOOL_LABEL_OVERRIDES[toolName];
    if (override) return override;

    return toolName.startsWith(ToolCallHelper.CONNECTOR_TOOL_PREFIX)
      ? ToolCallHelper.CONNECTOR_FALLBACK
      : undefined;
  }

  static getDisplayName(toolName: string): string {
    return ToolCallHelper.overrideVerb(toolName)?.infinitive ?? toolName;
  }

  /** Split `verb_noun_parts` into its verb form + a spaced noun. When the first segment isn't a known
   * verb, the whole name is the noun and the generic "Ran" verb is used. */
  private static parse(toolName: string): { verb: VerbForms; noun: string } {
    const override = ToolCallHelper.overrideVerb(toolName);
    if (override) return { verb: override, noun: '' };

    const segments = toolName.split('_').filter(Boolean);
    const [head, ...rest] = segments;
    const verb = head ? ToolCallHelper.VERBS[head] : undefined;
    if (verb && rest.length > 0) {
      return { verb, noun: rest.join(' ') };
    }
    return { verb: ToolCallHelper.FALLBACK, noun: segments.join(' ') };
  }

  /** Full activity label for the running/done states — e.g. "Checked documents". */
  static getActivityLabel(toolName: string, phase: ToolCallPhase): string {
    const { verb, noun } = ToolCallHelper.parse(toolName);
    const lead = phase === 'running' ? verb.running : verb.done;
    return noun ? `${lead} ${noun}` : lead;
  }

  /** Infinitive phrase for the failed state — rendered after a red "Failed to" — e.g.
   * "check documents". */
  static getFailureLabel(toolName: string): string {
    const { verb, noun } = ToolCallHelper.parse(toolName);
    return noun ? `${verb.infinitive} ${noun}` : verb.infinitive;
  }
}
